'use client';

import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import { MdEdit, MdInfo } from 'react-icons/md';
import { IoMdAddCircle } from 'react-icons/io';

import { ABSOLUTE_MIN_BID } from '@/lib/constants';
import {
  currentPhaseState,
  isUserPermittedState,
  isWLPhaseState,
  userPubkeyState,
  selectedProjectBidsState,
  selectedProjectBidStatsState,
} from '@/lib/state';
import { lamportsToSol, lamportsToToken } from '@/lib/utils';

import AutoBidButton from '@/components/AutoBidButton';
import Tabs from '@/components/Tabs';

const ERROR_MESSAGES = {
  amt: {
    required: 'This field is required',
    min: 'Bid is too small',
    pattern: 'Please enter a valid bid',
  },
  qty: {
    required: 'This field is required',
    min: 'Bids must be at least 1',
    pattern: 'Only whole numbers allowed',
  },
};

export default function BidForm({ project, onAddBid }) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const watchedAmt = watch('amt');

  const currentPhase = useRecoilValue(currentPhaseState);
  const isWLPhase = useRecoilValue(isWLPhaseState);
  const projectBids = useRecoilValue(selectedProjectBidsState);
  const projectStats = useRecoilValue(selectedProjectBidStatsState);
  const userPubkey = useRecoilValue(userPubkeyState);
  const isUserPermitted = useRecoilValue(isUserPermittedState);
  const { min_bid, token, decimals } = project;

  const atCapacity = useMemo(() => {
    const { total, ...rest } = projectBids || {};
    const all = Object.values(rest).reduce((acc, cur) => acc + cur, 0);
    return total <= all;
  }, [projectBids]);

  const lowestBid = useMemo(() => {
    const { min } = projectStats || {};
    return lamportsToToken(decimals, min);
  }, [decimals, projectStats]);

  const medianBid = useMemo(() => {
    const { percentiles } = projectStats || {};
    if (!percentiles) {
      return lowestBid;
    }
    const { lamports: median } = percentiles.find((x) => x.percentile === 50) || {};
    if (!median) {
      return lowestBid;
    }
    const medianPrice = lamportsToToken(decimals, median);
    if (medianPrice < lowestBid) {
      return lowestBid;
    }
    return medianPrice;
  }, [decimals, projectStats, lowestBid]);

  const lowestViable = useMemo(() => {
    // Only acknowledge phase price if it's not a public phase
    if (currentPhase?.price && currentPhase.phase !== 1) {
      return lamportsToToken(decimals, currentPhase.price);
    }
    return Number(lowestBid);
  }, [decimals, lowestBid, currentPhase]);

  const checkLowPrices = useCallback(
    (percentile = 10) => {
      if (!atCapacity) {
        return Number(lamportsToToken(decimals, projectStats?.min || ABSOLUTE_MIN_BID));
      }
      const { total, count, team } = projectBids || {};
      const { percentiles } = projectStats || {};
      const { lamports: threshold } = percentiles?.find((x) => x.percentile === percentile) || {};
      const solThreshold = lamportsToToken(decimals, threshold);
      return watchedAmt && count + team >= total && watchedAmt <= solThreshold;
    },
    [decimals, projectBids, watchedAmt, projectStats, atCapacity],
  );

  const showPriceError = useMemo(() => {
    if (currentPhase?.price) {
      return watchedAmt && watchedAmt < lamportsToToken(decimals, currentPhase.price);
    }
    return watchedAmt && watchedAmt < lowestBid;
  }, [decimals, currentPhase, watchedAmt, lowestBid]);

  const showPriceWarning = useMemo(() => {
    if (atCapacity) {
      return checkLowPrices(15);
    }
    return false;
  }, [atCapacity, checkLowPrices]);

  return (
    <Tabs
      tabs={[
        {
          id: 'quick',
          title: (
            <div className="flex items-center justify-center space-x-2 md:px-4">
              <IoMdAddCircle size={20} />
              <span>Quick Add</span>
            </div>
          ),
          content: (
            <div className="flex flex-col mt-4">
              <div className="flex items-center space-x-4">
                <AutoBidButton
                  value={lowestViable}
                  token={token}
                  onSubmit={() => onAddBid({ qty: 1, amt: lowestViable })}
                >
                  <div className="flex flex-col items-center justify-center">
                    <span className="font-bold">Min</span>
                    <span>
                      ({lowestViable} {token})
                    </span>
                  </div>
                </AutoBidButton>

                <AutoBidButton
                  value={medianBid}
                  token={token}
                  onSubmit={() => onAddBid({ qty: 1, amt: medianBid })}
                >
                  <div className="flex flex-col items-center justify-center">
                    <span className="font-bold">Median</span>
                    <span>
                      ({medianBid} {token})
                    </span>
                  </div>
                </AutoBidButton>
              </div>
            </div>
          ),
        },
        {
          id: 'custom',
          title: (
            <div className="flex items-center justify-center space-x-2 md:px-4">
              <MdEdit size={20} />
              <span>Custom Bid</span>
            </div>
          ),
          content: (
            <form className="flex flex-col w-full mt-4" onSubmit={handleSubmit(onAddBid)}>
              <p className="font-medium">Bid Price</p>

              <div className="form-control w-full">
                <label className="label">
                  {isWLPhase ? (
                    <div className="label-text">
                      WL Price:
                      <span className="font-mono">
                        {' '}
                        {lowestViable} {token}{' '}
                      </span>
                    </div>
                  ) : min_bid && !atCapacity ? (
                    <div className="flex items-center space-x-1">
                      <span className="label-text">
                        Minimum starting bid is: {lowestViable} {token}
                      </span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip={`Price bids in ${token} (up to 3 decmial places)`}
                      >
                        <MdInfo />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="label-text">
                        Minimum viable bid: {lowestBid} {token}
                      </span>
                      <div
                        className="tooltip tooltip-right"
                        data-tip="Smallest live winning bid price + the outbid step"
                      >
                        <MdInfo />
                      </div>
                    </div>
                  )}
                </label>
                <label className="input-group w-full">
                  <input
                    placeholder={isWLPhase ? lowestViable : lamportsToToken(decimals, min_bid) || 1}
                    className="input bg-neutral text-neutral-content w-full"
                    type="text"
                    {...register('amt', {
                      required: true,
                      pattern: /^(\d*\.?)?\d{1,4}$/,
                      min: Math.max(
                        lamportsToSol(ABSOLUTE_MIN_BID),
                        lamportsToToken(decimals, currentPhase?.price || min_bid || 0),
                        lowestViable || 0,
                      ),
                    })}
                  />
                  <span className="bg-neutral">{token}</span>
                </label>
                <label className="label">
                  {errors.amt && (
                    <span className="label-text-alt text-error">
                      {ERROR_MESSAGES.amt[errors.amt.type] || 'Please enter a valid bid'}
                    </span>
                  )}
                  {showPriceError && (
                    <span className="label-text-alt text-error">
                      This bid is below the live winning bid threshold
                    </span>
                  )}
                  {!showPriceError && showPriceWarning && (
                    <span className="label-text-alt text-warning">
                      This bid is in the bottom 15% of all live winning bids
                    </span>
                  )}
                </label>
              </div>
              <p className="font-medium">Number of Bids</p>
              <div className="form-control">
                <label className="label">
                  {isWLPhase && currentPhase?.user_max > 0 ? (
                    <div className="label-text">
                      WL Max:<span className="font-mono"> {currentPhase.user_max} bids </span> per
                      user
                    </div>
                  ) : (
                    <span className="label-text">How many bids at this price?</span>
                  )}
                </label>
                <input
                  placeholder={0}
                  className="input bg-neutral text-neutral-content"
                  type="number"
                  {...register('qty', {
                    required: true,
                    pattern: /^\d+\s*$/,
                    min: 1,
                  })}
                />
                <label className="label">
                  {errors.qty && (
                    <span className="label-text-alt text-error">
                      {ERROR_MESSAGES.qty[errors.qty.type] || 'Please enter a valid qty'}
                    </span>
                  )}
                </label>
              </div>
              <div className="flex w-full justify-end">
                {userPubkey && isUserPermitted ? (
                  <button className="btn btn-primary" type="submit">
                    Add Bid
                  </button>
                ) : !userPubkey ? (
                  <div className="btn btn-disabled">Connect Wallet</div>
                ) : !isUserPermitted && isWLPhase ? (
                  <div className="btn btn-disabled">Wallet not allowed</div>
                ) : !isUserPermitted ? (
                  <div className="btn btn-disabled">No bidding allowed</div>
                ) : (
                  <div className="btn btn-disabled">Connect Wallet</div>
                )}
              </div>
            </form>
          ),
        },
      ]}
    />
  );
}
